class Wheel {
    // Active button configuration
    activeButtons: string[];
    
    // Angle tracking for action triggering
    lastExecutedAngle: number;
    angleThreshold: number; // Degrees needed to trigger an action
    
    // Debouncing for smooth action execution
    debounceDelay: number;
    lastDebounceTime: number;
    
    // DOM and interaction state
    wheelElm: HTMLElement | null;
    isDragging: boolean;
    
    // Angle calculations
    currentAngle: number;
    oldAngle: number;
    lastAngles: number[];
    
    // Touch/mouse start positions
    startX: number | null;
    startY: number | null;
    startAngle: number;
    
    // Position and geometry
    wheelWidth: number = 0;
    wheelHeight: number = 0;
    wheelX: number = 0;
    wheelY: number = 0;
    
    // Animation and momentum
    isSpinning: boolean;
    animationId: number | null;
    
    // Speed and direction tracking
    speed: number;
    direction: number; // 1 for clockwise, -1 for counter-clockwise
    
    // Callbacks
    positionCallbacks: Function[];
    actionCallbacks: { [key: string]: Function };
    
    constructor(activeButtons: string[] = []) {
        // Initialize properties
        this.activeButtons = activeButtons;
        this.lastExecutedAngle = 0;
        this.angleThreshold = 15; // Degrees per action - adjust for sensitivity
        
        // Get wheel element
        this.wheelElm = document.getElementById('wheel');
        if (!this.wheelElm) {
            throw new Error('Wheel element with id "wheel" not found');
        }
        
        // Initialize state
        this.isDragging = false;
        this.currentAngle = 0;
        this.oldAngle = 0;
        this.lastAngles = Array(5).fill(0); // More samples for smoother momentum
        this.startX = null;
        this.startY = null;
        this.startAngle = 0;
        this.isSpinning = false;
        this.animationId = null;
        this.speed = 0;
        this.direction = 0;
        
        // Callbacks
        this.positionCallbacks = [];
        this.actionCallbacks = {};
        
        // Debouncing
        this.debounceDelay = 50; // Faster response, more like real MP3 players
        this.lastDebounceTime = 0;
        
        // Initialize
        this.calculatePositions();
        this.setupEventListeners();
    }
    
    private setupEventListeners(): void {
        // Mouse events
        this.wheelElm!.addEventListener('mousedown', (e) => {
            e.preventDefault();
            this.onGrab(e.clientX, e.clientY);
        });
        
        window.addEventListener('mousemove', (e) => {
            if (this.isDragging) {
                e.preventDefault();
                this.onMove(e.clientX, e.clientY);
            }
        });
        
        window.addEventListener('mouseup', () => {
            this.onRelease();
        });
        
        // Touch events
        this.wheelElm!.addEventListener('touchstart', (e) => {
            e.preventDefault();
            const touch = e.touches[0];
            this.onGrab(touch.clientX, touch.clientY);
        });
        
        window.addEventListener('touchmove', (e) => {
            if (this.isDragging) {
                e.preventDefault();
                const touch = e.touches[0];
                this.onMove(touch.clientX, touch.clientY);
            }
        });
        
        window.addEventListener('touchend', () => {
            this.onRelease();
        });
        
        // Window resize
        window.addEventListener('resize', () => {
            this.calculatePositions();
        });
        
        // Prevent context menu on wheel
        this.wheelElm!.addEventListener('contextmenu', (e) => {
            e.preventDefault();
        });
    }
    
    calculatePositions(): void {
        if (!this.wheelElm) return;
        
        const rect = this.wheelElm.getBoundingClientRect();
        this.wheelWidth = rect.width;
        this.wheelHeight = rect.height;
        this.wheelX = rect.left + this.wheelWidth / 2;
        this.wheelY = rect.top + this.wheelHeight / 2;
    }
    
    setActiveButtons(activeButtons: string[]): void {
        this.activeButtons = activeButtons;
    }
    
    setAngleThreshold(threshold: number): void {
        this.angleThreshold = Math.max(1, threshold);
    }
    
    onPositionChange(callback: Function): void {
        this.positionCallbacks.push(callback);
    }
    
    onAction(action: string, callback: Function): void {
        this.actionCallbacks[action] = callback;
    }
    
    private onGrab(x: number, y: number): void {
        if (this.isSpinning) {
            this.stopMomentum();
        }
        
        this.isDragging = true;
        this.startAngle = this.calculateAngle(x, y);
        this.lastAngles.fill(this.currentAngle);
        
        // Update positions in case element moved
        this.calculatePositions();
    }
    
    private onMove(x: number, y: number): void {
        if (!this.isDragging) return;
        
        // Update angle history for momentum calculation
        this.lastAngles.shift();
        this.lastAngles.push(this.currentAngle);
        
        const currentMouseAngle = this.calculateAngle(x, y);
        let deltaAngle = currentMouseAngle - this.startAngle;
        
        // Handle angle wrapping (crossing 0/360 boundary)
        if (deltaAngle > 180) {
            deltaAngle -= 360;
        } else if (deltaAngle < -180) {
            deltaAngle += 360;
        }
        
        this.currentAngle = this.oldAngle + deltaAngle;
        
        // Calculate movement since last action execution
        const angleMovement = this.currentAngle - this.lastExecutedAngle;
        
        // Check if we've moved enough to trigger an action
        if (Math.abs(angleMovement) >= this.angleThreshold) {
            const direction = angleMovement > 0 ? 'DOWN' : 'UP';
            const steps = Math.floor(Math.abs(angleMovement) / this.angleThreshold);
            
            if (this.activeButtons.includes(direction)) {
                this.executeAction(direction, steps);
            }
            
            // Update last executed angle
            this.lastExecutedAngle = this.currentAngle - (angleMovement % this.angleThreshold);
        }
        
        this.render(this.currentAngle);
    }
    
    private calculateAngle(x: number, y: number): number {
        const dx = x - this.wheelX;
        const dy = y - this.wheelY;
        let angle = Math.atan2(dx, -dy) * (180 / Math.PI);
        
        // Normalize angle to 0-360
        if (angle < 0) {
            angle += 360;
        }
        
        return angle;
    }
    
    private onRelease(): void {
        if (!this.isDragging) return;
        
        this.isDragging = false;
        this.oldAngle = this.currentAngle;
        
        // Calculate momentum from recent movement
        const recentAngles = this.lastAngles.slice(-3);
        const momentum = (recentAngles[2] - recentAngles[0]) / 2;
        
        if (Math.abs(momentum) > 0.5) {
            this.startMomentum(momentum);
        }
    }
    
    private startMomentum(initialSpeed: number): void {
        this.isSpinning = true;
        this.speed = Math.max(-8, Math.min(8, initialSpeed)); // Limit maximum speed
        this.giveMomentum();
    }
    
    private giveMomentum(): void {
        if (!this.isSpinning) return;
        
        const friction = 0.95; // Adjust for different deceleration
        const minSpeed = 0.1;
        
        this.speed *= friction;
        
        if (Math.abs(this.speed) < minSpeed) {
            this.stopMomentum();
            return;
        }
        
        this.oldAngle += this.speed;
        this.currentAngle = this.oldAngle;
        
        // Check for actions during momentum
        const angleMovement = this.currentAngle - this.lastExecutedAngle;
        if (Math.abs(angleMovement) >= this.angleThreshold) {
            const direction = angleMovement > 0 ? 'DOWN' : 'UP';
            const steps = Math.floor(Math.abs(angleMovement) / this.angleThreshold);
            
            if (this.activeButtons.includes(direction)) {
                this.executeAction(direction, steps);
            }
            
            this.lastExecutedAngle = this.currentAngle - (angleMovement % this.angleThreshold);
        }
        
        this.render(this.currentAngle);
        
        this.animationId = requestAnimationFrame(() => this.giveMomentum());
    }
    
    private stopMomentum(): void {
        this.isSpinning = false;
        this.speed = 0;
        
        if (this.animationId !== null) {
            cancelAnimationFrame(this.animationId);
            this.animationId = null;
        }
    }
    
    private executeAction(action: string, steps: number = 1): void {
        const now = Date.now();
        if (now - this.lastDebounceTime < this.debounceDelay) {
            return;
        }
        
        this.lastDebounceTime = now;
        
        // Execute the appropriate action
        if (this.actionCallbacks[action]) {
            this.actionCallbacks[action](steps);
        } else {
            // Fallback to legacy methods
            if (action === 'UP' && this.handleUp) {
                for (let i = 0; i < steps; i++) {
                    this.handleUp();
                }
            } else if (action === 'DOWN' && this.handleDown) {
                for (let i = 0; i < steps; i++) {
                    this.handleDown();
                }
            }
        }
    }
    
    // Legacy method stubs - implement these or use onAction instead
    handleUp(): void {
        console.log('Handle UP action');
    }
    
    handleDown(): void {
        console.log('Handle DOWN action');
    }
    
    private render(angle: number): void {
        if (!this.wheelElm) return;
        
        // Update speed and direction for external access
        const recentSpeed = this.lastAngles[this.lastAngles.length - 1] - this.lastAngles[this.lastAngles.length - 3];
        this.direction = recentSpeed > 0 ? 1 : recentSpeed < 0 ? -1 : 0;
        
        // Apply visual rotation
        this.wheelElm.style.transform = `rotate(${angle}deg)`;
        
        // Optional haptic feedback (only works on mobile)
        if (navigator.vibrate && Math.abs(recentSpeed) > 1) {
            navigator.vibrate(10);
        }
        
        // Execute position callbacks
        this.positionCallbacks.forEach(callback => {
            try {
                callback(angle, this.direction, Math.abs(recentSpeed));
            } catch (error) {
                console.error('Error in position callback:', error);
            }
        });
    }
    
    // Public getters
    getSpeed(): number {
        return Math.abs(this.speed);
    }
    
    getDirection(): number {
        return this.direction;
    }
    
    getCurrentAngle(): number {
        return this.currentAngle;
    }
    
    isCurrentlySpinning(): boolean {
        return this.isSpinning;
    }
    
    // Public methods for external control
    reset(): void {
        this.stopMomentum();
        this.currentAngle = 0;
        this.oldAngle = 0;
        this.lastExecutedAngle = 0;
        this.render(0);
    }
    
    destroy(): void {
        this.stopMomentum();
        // Remove event listeners would go here if we stored references
    }
}

export default Wheel;